import React, { useState, useEffect } from 'react';
import { 
  FiSearch, FiFilter, FiDownload, FiActivity, FiDatabase,
  FiChevronLeft, FiChevronRight, FiCheckCircle, FiAlertCircle, FiPlus, FiLoader
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import InstrumentSelector from '../../../../components/common/InstrumentSelector';
import { services } from '../../../../services/api';
import useBreakpoint from '../../../../hooks/useBreakpoint';

const HistoricalDataInstrumentList = ({ onSelectInstrument }) => {
  //! Phone gets CARDS, not the table. The 8-column table wants ~900px+ and only
  //! "fits" a 375px screen by panning sideways ~2.8x, which reads as broken and
  //! cut off. The hover-only "View" action is also invisible on touch, so the
  //! card carries its own always-visible affordance. Desktop keeps the table.
  const { isPhone } = useBreakpoint();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterExchange, setFilterExchange] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real data from MDMS backend
  const [instruments, setInstruments] = useState([]);
  const [pagination, setPagination] = useState({ skip: 0, limit: 100, total: 0 });

  // Fetch instruments from MDMS backend
  useEffect(() => {
    fetchInstruments();
  }, [pagination.skip, filterExchange]);

  const fetchInstruments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        skip: pagination.skip,
        limit: pagination.limit,
      };
      
      if (filterExchange !== 'all') {
        params.exchange = filterExchange.toUpperCase();
      }
      
      const response = await services.marketData.listInstruments(params);
      
      // Transform backend response - MDMS returns OHLC data for display
      // Broker selection fields will be fetched from Instrument Master when user selects instrument
      const transformedInstruments = response.data.instruments.map(inst => ({
        id: inst.instrument_id,
        instrument_id: inst.instrument_id,
        symbol: inst.symbol,
        name: inst.symbol,
        composite_key: inst.composite_key || 'N/A',
        exchange_token: inst.exchange_token || 'N/A',
        instrument_type: inst.instrument_type || 'EQUITY',
        type: inst.instrument_type || 'EQUITY',
        exchange: inst.exchange,
        expiry_date: inst.expiry_date,
        strike_price: inst.strike_price,
        option_type: inst.option_type,
        // UI display fields from MDMS
        hasTick: inst.total_records > 0,
        hasMinute: inst.total_records > 0,
        range: formatDataRange(inst.first_data_time, inst.last_data_time),
        status: determineStatus(inst.last_data_time),
        total_records: inst.total_records,
        first_data_time: inst.first_data_time,
        last_data_time: inst.last_data_time,
      }));
      
      setInstruments(transformedInstruments);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (err) {
      console.error('Error fetching instruments:', err);
      setError(err.response?.data?.detail || 'Failed to fetch instruments from MDMS');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format data range
  const formatDataRange = (firstTime, lastTime) => {
    if (!firstTime || !lastTime) return 'No Data';
    
    const first = new Date(firstTime);
    const last = new Date(lastTime);
    const years = last.getFullYear() - first.getFullYear();
    
    if (years > 0) {
      return `${first.getFullYear()} - ${last.getFullYear()} (${years} Yrs)`;
    } else {
      const months = (last.getMonth() - first.getMonth()) + 1;
      return `${months} Month${months > 1 ? 's' : ''} (${first.getFullYear()})`;
    }
  };

  // Helper function to determine status based on last data time
  const determineStatus = (lastTime) => {
    if (!lastTime) return 'stale';
    
    const last = new Date(lastTime);
    const now = new Date();
    const daysDiff = (now - last) / (1000 * 60 * 60 * 24);
    
    if (daysDiff < 7) return 'live';
    if (daysDiff < 30) return 'partial';
    return 'stale';
  };

  const filteredInstruments = instruments.filter(inst => {
    const matchesSearch = inst.symbol.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || (inst.type && inst.type.toLowerCase() === filterType.toLowerCase());
    return matchesSearch && matchesType;
  });

  const handleAddInstrument = (payload) => {
      // payload is { instrument, brokerSource } from InstrumentSelector
      const instData = payload.instrument || payload;
      
      const instToAdd = {
          id: instData.instrument_id || Date.now(),
          instrument_id: instData.instrument_id,
          symbol: instData.symbol || instData.name || 'UNKNOWN',
          type: instData.instrument_type || 'Equity',
          exchange: instData.exchange || 'NSE',
          hasTick: false,
          hasMinute: false,
          range: 'Not Synced',
          status: 'pending',
          total_records: 0,
      };
      setInstruments(prev => [instToAdd, ...prev]);
      setIsAddModalOpen(false);
  };

  //--> Shared empty state for both presentations.
  const emptyState = (
    <div className="p-12 text-center text-fg-3">
      <FiFilter className="mx-auto mb-4 text-4xl opacity-20" />
      <p>No instruments match your filters</p>
    </div>
  );

  //--> Absolute date for the phone cards' First/Last labels. The table's
  //    `range` string ("2019 - 2024 (5 Yrs)") is a span, not an endpoint.
  const formatDataTime = (time) => {
    if (!time) return '—';
    return new Date(time).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  //--> PHONE: one card per instrument. The whole card is the tap target and
  //    routes through the same onSelectInstrument the table row uses.
  const renderInstrumentCards = () => (
    <div className="flex flex-col gap-3">
      {filteredInstruments.map((inst) => (
        <motion.button
          key={inst.id}
          type="button"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => onSelectInstrument(inst)}
          className="w-full text-left rounded-xl border border-[var(--divider)] bg-bg-2 p-4 active:bg-accent/5 transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="p-2 bg-accent/10 rounded-lg text-accent shrink-0">
              <FiDatabase />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-fg-1">{inst.symbol}</span>
                {inst.is_grouped && (
                  <span className="ds-badge xs role">Continuous</span>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className="ds-badge xs neutral font-mono">
                  {inst.exchange}
                </span>
                <span className="text-xs text-fg-2 font-mono">
                  Token: {inst.exchange_token}
                </span>
              </div>
            </div>
            <span className={`ds-badge sm shrink-0 ${
              inst.type === 'EQUITY' ? 'info' :
              inst.type === 'FUTURE' ? 'role' :
              inst.type === 'OPTION' ? 'warn' :
              'neutral'
            }`}>
              {inst.type || 'EQUITY'}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3 border-t border-[var(--divider)] pt-3">
            <div className="min-w-0">
              <div className="ds-eyebrow text-fg-3">Records</div>
              <div className="mt-0.5 text-sm font-semibold text-fg-1 truncate">
                {inst.total_records?.toLocaleString() || '0'}
              </div>
            </div>
            <div className="min-w-0">
              <div className="ds-eyebrow text-fg-3">First</div>
              <div className="mt-0.5 text-xs font-mono text-fg-2 truncate">
                {formatDataTime(inst.first_data_time)}
              </div>
            </div>
            <div className="min-w-0">
              <div className="ds-eyebrow text-fg-3">Last</div>
              <div className="mt-0.5 text-xs font-mono text-fg-2 truncate">
                {formatDataTime(inst.last_data_time)}
              </div>
            </div>
          </div>

          {/* Always-visible affordance. The table's equivalent is
              opacity-0 group-hover:opacity-100, which never resolves on touch. */}
          <div className="mt-3 flex items-center justify-between gap-2">
            {inst.total_records > 0 ? (
              <span className="ds-badge sm profit">
                <FiCheckCircle size={13} />
                Data available
              </span>
            ) : (
              <span className="ds-badge sm neutral">No Data</span>
            )}
            <span className="flex items-center gap-1 text-sm font-bold text-accent">
              View <FiChevronRight />
            </span>
          </div>
        </motion.button>
      ))}
      {filteredInstruments.length === 0 && (
        <div className="rounded-xl border border-[var(--divider)] bg-bg-2">{emptyState}</div>
      )}
    </div>
  );

  //--> Pagination — shared by both presentations.
  const renderPagination = () => {
    if (pagination.total <= 0) return null;
    return (
      <div className={`px-4 py-3 sm:px-6 sm:py-4 bg-bg-3/50 ${
        isPhone
          ? 'rounded-xl border border-[var(--divider)]'
          : 'border-t border-[var(--divider)]'
      }`}>
        {/* flex-wrap: the "Showing N of N instruments" label and the page
            controls together are wider than a phone card in one row. */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm text-fg-2">
          <span>
            Showing {filteredInstruments.length} of {pagination.total} instruments
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
              disabled={pagination.skip === 0}
              aria-label="Previous page"
              className="btn btn-secondary btn-icon"
            >
              <FiChevronLeft />
            </button>
            <span className="font-semibold">
              Page {Math.floor(pagination.skip / pagination.limit) + 1}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
              disabled={pagination.skip + pagination.limit >= pagination.total}
              aria-label="Next page"
              className="btn btn-secondary btn-icon"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-loss/10 border border-loss/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-loss">
            <FiAlertCircle />
            <span className="font-semibold">Error loading instruments</span>
          </div>
          <p className="text-loss text-sm mt-1">{error}</p>
          <button
            onClick={fetchInstruments}
            className="mt-2 text-sm text-loss underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Filters Toolbar */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-bg-2 p-4 rounded-2xl shadow-sm border border-[var(--divider)]">
        <div className="relative w-full md:w-96">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-3" />
          <input
            type="text"
            placeholder="Search instruments..."
            className="w-full ds-input !pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {/* Wraps on phone: the exchange select, the 6-pill type strip and the Add
            button used to be pinned in one non-wrapping row, which crushed the
            pills to a ~60px sliver. Each block now takes a full row at <md. */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 w-full md:w-auto">
            {/* Exchange Filter */}
            <div className="flex items-center gap-2">
              <select
                value={filterExchange}
                onChange={(e) => setFilterExchange(e.target.value)}
                className="ds-input font-semibold w-full md:w-auto"
              >
                <option value="all">All Exchanges</option>
                <option value="nse">NSE</option>
                <option value="bse">BSE</option>
                <option value="mcx">MCX</option>
                <option value="nfo">NFO</option>
                <option value="cds">CDS</option>
              </select>
            </div>

            {/* Type Filter */}
            {/* flex-wrap on phone, not a sideways scroller: 6 pills in a 60px
                sliver is unusable, and a nested scroller inside a wrapping
                toolbar is worse than just letting them run onto a second row. */}
            <div className="flex flex-wrap items-center gap-2 md:flex-nowrap md:overflow-x-auto md:pb-0">
              {['All', 'Index', 'Equity', 'Future', 'Option', 'Currency'].map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type.toLowerCase())}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                    filterType === type.toLowerCase()
                      ? 'bg-accent text-white shadow-md shadow-accent/20 dark:shadow-none'
                      : 'bg-bg-3 text-fg-2 hover:bg-bg-3 dark:hover:bg-bg-3'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>

            <button
                type="button"
                onClick={() => setIsAddModalOpen(true)}
                className="btn btn-primary btn-sm flex w-full items-center justify-center gap-2 whitespace-nowrap md:w-auto"
            >
                <FiPlus size={18} /> Add Instrument
            </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-bg-2 rounded-2xl shadow-sm border border-[var(--divider)] p-12">
          <div className="flex flex-col items-center justify-center text-fg-3">
            <FiLoader className="animate-spin text-4xl mb-4" />
            <p>Loading instruments from MDMS...</p>
          </div>
        </div>
      )}

      {/* Instruments — cards on phone, table on desktop */}
      {!loading && !error && (
        isPhone ? (
          <div className="space-y-4">
            {renderInstrumentCards()}
            {renderPagination()}
          </div>
        ) : (
        <div className="bg-bg-2 rounded-2xl shadow-sm border border-[var(--divider)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-bg-3/50">
                <tr>
                  <th className="px-6 py-4 text-left ds-eyebrow text-fg-2">Instrument</th>
                  <th className="px-6 py-4 text-left ds-eyebrow text-fg-2">Exchange/Token</th>
                  <th className="px-6 py-4 text-left ds-eyebrow text-fg-2">Type</th>
                  <th className="px-6 py-4 text-left ds-eyebrow text-fg-2">Derivatives Info</th>
                  <th className="px-6 py-4 text-center ds-eyebrow text-fg-2">Data Status</th>
                  <th className="px-6 py-4 text-left ds-eyebrow text-fg-2">Data Range</th>
                  <th className="px-6 py-4 text-left ds-eyebrow text-fg-2">Records</th>
                  <th className="px-6 py-4 text-right ds-eyebrow text-fg-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--divider)]">
                {/* Row hover moved off framer's whileHover and onto a class: the
                    animated value was a hardcoded indigo rgba() and framer can't
                    tween to a CSS var, so the themed accent only reaches this
                    row as a Tailwind hover. */}
                {filteredInstruments.map((inst) => (
                  <motion.tr
                    key={inst.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="group cursor-pointer transition-colors hover:bg-accent/5"
                    onClick={() => onSelectInstrument(inst)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-accent/10 rounded-lg text-accent">
                          <FiDatabase />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="font-bold text-fg-1">{inst.symbol}</div>
                            {inst.is_grouped && (
                              <span className="ds-badge xs role">
                                Continuous
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-fg-2 font-mono mt-0.5">
                            ID: {inst.instrument_id.substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <span className="ds-badge xs neutral font-mono">
                          {inst.exchange}
                        </span>
                        <div className="text-xs text-fg-2 mt-1 font-mono">
                          Token: {inst.exchange_token}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`ds-badge sm ${
                        inst.type === 'EQUITY' ? 'info' :
                        inst.type === 'FUTURE' ? 'role' :
                        inst.type === 'OPTION' ? 'warn' :
                        'neutral'
                      }`}>
                        {inst.type || 'EQUITY'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {inst.is_grouped && inst.expiry_count ? (
                        <div className="text-xs text-fg-2">
                          <div className="font-semibold text-accent">
                            {inst.expiry_count} {inst.expiry_count === 1 ? 'contract' : 'contracts'}
                          </div>
                          {inst.min_expiry && inst.max_expiry && (
                            <div className="mt-1 text-fg-3">
                              {new Date(inst.min_expiry).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                              {' - '}
                              {new Date(inst.max_expiry).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                            </div>
                          )}
                        </div>
                      ) : inst.expiry_date || inst.strike_price ? (
                        <div className="text-xs text-fg-2">
                          {inst.expiry_date && <div className="font-semibold">Expiry: {new Date(inst.expiry_date).toLocaleDateString()}</div>}
                          {inst.strike_price && <div>Strike: ₹{inst.strike_price}</div>}
                          {inst.option_type && (
                            <span className={`ds-badge xs mt-1 ${
                              inst.option_type === 'CE' ? 'profit' : 'loss'
                            }`}>
                              {inst.option_type === 'CE' ? 'CALL' : 'PUT'}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-fg-3">-</div>
                      )}
                    </td>
                    
                    {/* Data Available Status */}
                    <td className="px-6 py-4 text-center">
                      {inst.total_records > 0 ? (
                        <span className="ds-badge sm profit">
                          <FiCheckCircle size={13} />
                          Yes
                        </span>
                      ) : (
                        <span className="ds-badge sm neutral">
                          No Data
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm text-fg-2 font-mono font-medium">
                      {inst.range}
                    </td>
                    
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold text-fg-2">
                        {inst.total_records?.toLocaleString() || '0'}
                      </span>
                    </td>
                    
                    <td className="px-6 py-4 text-right">
                      <button className="text-accent hover:text-accent/80 font-bold text-sm flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-auto">
                         View <FiChevronRight />
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
            
            {filteredInstruments.length === 0 && emptyState}
          </div>

          {renderPagination()}
        </div>
        )
      )}

        {/* Add Instrument Modal */}
        <InstrumentSelector 
            open={isAddModalOpen}
            onSelect={(inst) => handleAddInstrument(inst)} 
            onClose={() => setIsAddModalOpen(false)}
            title="Add Instrument to Historical Data"
        />
    </div>
  );
};

export default HistoricalDataInstrumentList;
